/**
 * Next screen after Run AI: shows up to three ranked design options from intake.
 */
import React, { useEffect, useRef, useState } from 'react';
import {
    CaretLeft,
    CaretRight,
    Check,
    CircleNotch,
    Cube,
    Info,
    Sparkle,
    Tree,
    WarningCircle,
} from '@phosphor-icons/react';
import {
    getTreeCalculatorBandsForTree,
    TREE_CART_DEFAULT_HAUL,
    TREE_CART_DEFAULT_LOCATION,
    TREE_CART_DEFAULT_NO_OBST,
    TREE_CART_DEFAULT_STUMP,
} from '../../services/treeCartService';
import { getPreContractModuleShadeColor, getPreContractShadeColor } from '../lib/preContractModuleLayout';
import {
    generatePreContractAiDesigns,
    intakePatchFromAiDesign,
    loadPreContractAiOptionsCache,
    parsePreContractTargetKwh,
    persistPreContractAiOptionsCache,
    type PreContractAiDesignOption,
    type PreContractAiProgress,
} from '../lib/preContractAiDesigns';
import type { PreContractIntakeForm, PreContractRoofModuleLayout } from '../lib/preContractTypes';
import { fillOrientationLabel, normalizeFillOrientations } from '../lib/preContractTypes';
import type {
    AiDesignOptionCardProps,
    PreContractAiDesignsStepProps,
    PreContractAiResultTab,
} from './PreContractAiDesignsStep.types';

export type { PreContractAiDesignsStepProps };

const MIN_LOADING_MS = 350;

function formatKwh(value: number): string {
    if (!Number.isFinite(value) || value <= 0) return '—';
    return `${Math.round(value).toLocaleString()} kWh`;
}

function formatKw(value: number): string {
    if (!Number.isFinite(value) || value <= 0) return '—';
    return `${value.toFixed(2)} kW`;
}

function formatCutCost(value: number | null | undefined): string {
    if (value == null || !Number.isFinite(value)) return '—';
    return `$${Math.round(value).toLocaleString()}`;
}

export const PreContractAiDesignsStep: React.FC<PreContractAiDesignsStepProps> = ({
    form,
    patchForm,
    onBack,
    onApply,
    onNext,
    onOpenViewport,
    onPreviewAiOption,
    viewportAvailable = true,
    actionsDisabled = false,
    embedded = false,
}) => {
    // ----- Store -----
    const [layoutOptions, setLayoutOptions] = useState<PreContractAiDesignOption[]>([]);
    const [treeCutOptions, setTreeCutOptions] = useState<PreContractAiDesignOption[]>([]);
    const [resultTab, setResultTab] = useState<PreContractAiResultTab>('layouts');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [progress, setProgress] = useState<PreContractAiProgress>('layout');
    const [treeCutDefaultsOpen, setTreeCutDefaultsOpen] = useState(false);

    const pool = resultTab === 'trees' ? treeCutOptions : layoutOptions;
    const visibleOptions = pool.slice(0, 2);

    const applyOption = (option: PreContractAiDesignOption) => {
        patchForm(intakePatchFromAiDesign(option, form.siteTrees));
        onPreviewAiOption?.(option);
    };

    // ----- Generate options from the intake snapshot -----
    useEffect(() => {
        let cancelled = false;
        const cached = loadPreContractAiOptionsCache();
        if (cached) {
            const cachedTab =
                cached.selectedTab === 'trees' && cached.treeCutOptions.length > 0 ? 'trees' : 'layouts';
            const cachedPool = cachedTab === 'trees' ? cached.treeCutOptions : cached.layoutOptions;
            const cachedSelected =
                cachedPool.find((item) => item.id === cached.selectedId) ?? cachedPool[0] ?? null;
            setLayoutOptions(cached.layoutOptions);
            setTreeCutOptions(cached.treeCutOptions);
            setResultTab(cachedTab);
            setSelectedId(cachedSelected?.id ?? null);
            setLoading(false);
            if (cachedSelected) {
                patchForm(intakePatchFromAiDesign(cachedSelected, form.siteTrees));
            }
            return undefined;
        }

        const started = Date.now();
        setLoading(true);
        setError(null);

        void (async () => {
            try {
                const next = await generatePreContractAiDesigns(form, { onProgress: setProgress });
                const wait = Math.max(0, MIN_LOADING_MS - (Date.now() - started));
                if (wait > 0) {
                    await new Promise((resolve) => window.setTimeout(resolve, wait));
                }
                if (cancelled) return;
                const first = next.layoutOptions[0] ?? null;
                setLayoutOptions(next.layoutOptions);
                setTreeCutOptions(next.treeCutOptions);
                setResultTab('layouts');
                setSelectedId(first?.id ?? null);
                persistPreContractAiOptionsCache(
                    next.layoutOptions,
                    next.treeCutOptions,
                    first?.id ?? null,
                    'layouts',
                );
                if (first) {
                    patchForm(intakePatchFromAiDesign(first, form.siteTrees));
                }
            } catch (err) {
                if (!cancelled) {
                    console.error('Pre-contract AI designs failed', err);
                    setLayoutOptions([]);
                    setTreeCutOptions([]);
                    setSelectedId(null);
                    setError('Could not generate AI design options from this intake.');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
        // Snapshot the intake at the moment Run AI opens this screen.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ----- Handlers -----
    const handleSelectDesign = (id: string) => {
        const option = visibleOptions.find((item) => item.id === id);
        if (!option) return;
        setSelectedId(id);
        persistPreContractAiOptionsCache(layoutOptions, treeCutOptions, id, resultTab);
        applyOption(option);
    };

    const handleOpen3dView = (id: string) => {
        const option = visibleOptions.find((item) => item.id === id);
        if (!option) return;
        handleSelectDesign(id);
        if (viewportAvailable) onOpenViewport(option);
    };

    const handleResultTab = (tab: PreContractAiResultTab) => {
        if (tab === resultTab) return;
        if (tab !== 'trees') setTreeCutDefaultsOpen(false);
        const nextOptions = tab === 'trees' ? treeCutOptions : layoutOptions;
        const nextSelected =
            nextOptions.find((item) => item.id === selectedId)?.id ?? nextOptions[0]?.id ?? null;
        setResultTab(tab);
        setSelectedId(nextSelected);
        persistPreContractAiOptionsCache(layoutOptions, treeCutOptions, nextSelected, tab);
        const option = nextOptions.find((item) => item.id === nextSelected);
        if (option) applyOption(option);
    };

    const showLayoutsTab = () => handleResultTab('layouts');
    const showTreesTab = () => handleResultTab('trees');

    const handleApplyDesign = (id?: string) => {
        const targetId = id ?? selectedId;
        const option = visibleOptions.find((item) => item.id === targetId);
        if (!option) return;
        applyOption(option);
        persistPreContractAiOptionsCache(layoutOptions, treeCutOptions, option.id, resultTab);
        onApply(option);
    };

    const handleNext = () => {
        handleApplyDesign();
        onNext?.();
    };

    const selected = visibleOptions.find((item) => item.id === selectedId) ?? null;
    const actionsReady = !actionsDisabled && !loading && selected != null;
    const shadedPanelNote = [
        !form.includeGreyPanels ? 'skipping grey panels' : '',
        !form.includePurplePanels ? 'skipping purple panels' : '',
    ]
        .filter(Boolean)
        .join(', ');
    const grayNote = shadedPanelNote ? `, and ${shadedPanelNote}` : '';
    const selectedFills = normalizeFillOrientations(form.fillOrientations);
    const fillNote =
        selectedFills.length > 0
            ? `, ${selectedFills.map((item) => fillOrientationLabel(item).toLowerCase()).join(' / ')} max-fill`
            : '';
    const fillList = selectedFills.map((item) => fillOrientationLabel(item).toLowerCase()).join(', ');
    const progressCopy =
        progress === 'maxfill'
            ? {
                  title: 'Preparing module layout…',
                  detail: `Running max-fill for ${fillList || 'your selected orientation'}.`,
              }
            : progress === 'layout'
              ? {
                    title: 'Building recommended design…',
                    detail:
                        'Choosing the best module set from your configuration, roofs, usage, and offset preference.',
                }
              : progress === 'gemini'
                ? {
                      title: 'Asking AI for the best layout…',
                      detail: 'Gemini is choosing module sets from the max-fill layouts.',
                  }
                : progress === 'treecut'
                  ? {
                        title: 'Applying tree cutting…',
                        detail:
                            'Ghosting the highest-multiplier tree and re-measuring layouts, plus estimated cut cost.',
                    }
                  : {
                        title: 'Measuring production in OneDraw…',
                        detail: 'Placing the recommended layout in the 3D viewer and reading kWh Actual.',
                    };
    const targetKwh = parsePreContractTargetKwh(form.annualUsageKwh);
    const targetLabel = targetKwh > 0 ? `${Math.round(targetKwh).toLocaleString()} kWh/yr` : null;
    const offsetAim =
        form.offsetPreference === 'balanced' && targetKwh > 0
            ? 'Closest to usage, using the strongest modules'
            : form.offsetPreference === 'full' && targetKwh > 0
              ? 'Aiming to cover annual usage'
              : null;
    const showTreeTab = !loading && treeCutOptions.length > 0;

    // ----- Render -----
    return (
        <div
            id="pci-section-ai"
            className={`pci-ai-designs${embedded ? ' pci-ai-designs--embedded' : ''}${
                visibleOptions.length === 2 ? ' pci-ai-designs--duo' : ''
            }`}
        >
            <section className="pci-ai-designs__hero">
                <div className="pci-ai-designs__hero-copy">
                    <div className="pci-ai-designs__hero-badge">
                        <Sparkle size={14} weight="fill" aria-hidden />
                        AI layout studio
                    </div>
                    <h2 className="pci-ai-designs__hero-title">
                        {resultTab === 'trees'
                            ? 'Best layout with tree cutting'
                            : 'Choose your top layout'}
                    </h2>
                    <p className="pci-ai-designs__hero-lead">
                        {resultTab === 'trees'
                            ? 'Compare the recommended layout after ghosting the highest-impact tree.'
                            : `We ranked ${visibleOptions.length} design${
                                  visibleOptions.length === 1 ? '' : 's'
                              } from your roofs, modules, usage${grayNote}${fillNote}. Pick one to apply to Solar.`}
                    </p>
                    {targetLabel ? (
                        <div className="pci-ai-designs__hero-targets">
                            <span className="pci-ai-designs__hero-chip">
                                Target <strong>{targetLabel}</strong>
                            </span>
                            {offsetAim ? (
                                <span className="pci-ai-designs__hero-chip pci-ai-designs__hero-chip--muted">
                                    {offsetAim}
                                </span>
                            ) : null}
                        </div>
                    ) : null}
                </div>
                {embedded && selected ? (
                    <div className="pci-ai-designs__hero-actions">
                        <button
                            type="button"
                            className="pci-ai-designs__apply-btn"
                            disabled={!actionsReady}
                            onClick={() => handleApplyDesign()}
                        >
                            <Check size={16} weight="bold" aria-hidden />
                            Apply to Solar
                        </button>
                    </div>
                ) : null}
            </section>

            {showTreeTab ? (
                <div className="pci-ai-designs__tabs" role="tablist" aria-label="AI result views">
                    <button
                        type="button"
                        role="tab"
                        className={`pci-ai-designs__tab${
                            resultTab === 'layouts' ? ' pci-ai-designs__tab--on' : ''
                        }`}
                        aria-selected={resultTab === 'layouts'}
                        onClick={showLayoutsTab}
                    >
                        Layouts
                    </button>
                    <div className="pci-ai-designs__tab-with-info">
                        <button
                            type="button"
                            role="tab"
                            className={`pci-ai-designs__tab${
                                resultTab === 'trees' ? ' pci-ai-designs__tab--on' : ''
                            }`}
                            aria-selected={resultTab === 'trees'}
                            onClick={showTreesTab}
                        >
                            <Tree size={14} weight="fill" />
                            Tree Cutting
                        </button>
                        <TreeCutDefaultsInfo
                            open={treeCutDefaultsOpen}
                            onOpenChange={(open) => {
                                setTreeCutDefaultsOpen(open);
                                if (open && resultTab !== 'trees') showTreesTab();
                            }}
                            form={form}
                            treeCutOptions={treeCutOptions}
                        />
                    </div>
                </div>
            ) : null}

            {loading ? (
                <div className="pci-ai-designs__loading" role="status">
                    <CircleNotch className="pci-ai-designs__spinner" size={28} weight="bold" />
                    <strong>{progressCopy.title}</strong>
                    <span>
                        {progressCopy.detail}
                        {targetLabel ? ` Target ${targetLabel}.` : ''}
                    </span>
                </div>
            ) : null}

            {!loading && error ? (
                <div className="pci-ai-designs__empty" role="alert">
                    <WarningCircle size={20} weight="fill" />
                    <p>{error}</p>
                </div>
            ) : null}

            {!loading && !error && visibleOptions.length === 0 ? (
                <div className="pci-ai-designs__empty" role="status">
                    <WarningCircle size={20} weight="fill" />
                    <p>
                        {resultTab === 'trees'
                            ? 'No high-multiplier trees to cut for this site.'
                            : 'No design options could be built from the current module layout.'}
                    </p>
                </div>
            ) : null}

            {!loading && visibleOptions.length > 0 ? (
                <div className="pci-ai-designs__grid">
                    {visibleOptions.map((option, index) => (
                        <AiDesignOptionCard
                            key={option.id}
                            option={option}
                            optionNumber={index + 1}
                            rankLabel={index === 0 ? 'Top pick' : 'Runner-up'}
                            selected={option.id === selectedId}
                            onSelect={handleSelectDesign}
                            onApply={handleApplyDesign}
                            onOpen3dView={handleOpen3dView}
                            viewportAvailable={viewportAvailable}
                            targetKwh={targetKwh}
                            applyDisabled={actionsDisabled}
                        />
                    ))}
                </div>
            ) : null}

            {embedded && !loading && visibleOptions.length > 0 ? (
                <footer className="pci-ai-designs__apply-bar" aria-label="Apply design">
                    <div className="pci-ai-designs__apply-summary">
                        {selected ? (
                            <>
                                <Sparkle size={16} weight="fill" aria-hidden />
                                <div>
                                    <strong>{selected.title || `Design ${selected.rank}`}</strong>
                                    <span>
                                        {formatKwh(selected.annualKwh)} · {formatKw(selected.systemKw)} ·{' '}
                                        {selected.moduleCount} modules
                                    </span>
                                </div>
                            </>
                        ) : (
                            <span>Select a design to apply it to the Solar product card.</span>
                        )}
                    </div>
                    <button
                        type="button"
                        className="pci-ai-designs__apply-btn pci-ai-designs__apply-btn--wide"
                        disabled={!actionsReady}
                        onClick={() => handleApplyDesign()}
                    >
                        Apply to Solar design
                        <CaretRight size={16} weight="bold" aria-hidden />
                    </button>
                </footer>
            ) : null}

            {!embedded ? (
            <div className="pci-board-fab-dock" aria-label="Board actions">
                <div className="pci-board-fab-dock__start">
                    <button
                        type="button"
                        className="pci-board-fab__btn"
                        onClick={onBack}
                        title="Back"
                        aria-label="Back"
                    >
                        <CaretLeft size={20} weight="bold" />
                    </button>
                </div>
                <div className="pci-board-fab-dock__main" aria-hidden />
                <div className="pci-board-fab-dock__end">
                    <button
                        type="button"
                        className="pci-board-fab__btn pci-board-fab__btn--primary"
                        disabled={!actionsReady}
                        onClick={handleNext}
                        title="Next"
                        aria-label="Next"
                    >
                        <CaretRight size={20} weight="bold" />
                    </button>
                </div>
            </div>
            ) : null}
        </div>
    );
};

function cutTreeCalculatorLabels(
    form: PreContractIntakeForm,
    treeCutOptions: PreContractAiDesignOption[],
): { dbh: string; height: string } {
    const option = treeCutOptions[0];
    const treeId = option?.treeCut?.treeIds[0];
    const tree = treeId ? form.siteTrees.find((item) => item.id === treeId) : undefined;
    const computed = tree ? getTreeCalculatorBandsForTree(tree) : { dbhRange: null, heightRange: null };
    const dbh = option?.treeCut?.dbhRange || computed.dbhRange;
    const height = option?.treeCut?.heightRange || computed.heightRange;
    return {
        dbh: dbh ?? 'From cut tree',
        height: height ? `${height} ft` : 'From cut tree',
    };
}

function TreeCutDefaultsInfo({
    open,
    onOpenChange,
    form,
    treeCutOptions,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    form: PreContractIntakeForm;
    treeCutOptions: PreContractAiDesignOption[];
}) {
    const wrapRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return undefined;
        const onPointer = (event: MouseEvent) => {
            if (!wrapRef.current?.contains(event.target as Node)) onOpenChange(false);
        };
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onOpenChange(false);
        };
        document.addEventListener('mousedown', onPointer);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onPointer);
            document.removeEventListener('keydown', onKey);
        };
    }, [open, onOpenChange]);

    const bands = cutTreeCalculatorLabels(form, treeCutOptions);
    const rows: Array<{ label: string; value: string }> = [
        { label: 'Location', value: TREE_CART_DEFAULT_LOCATION },
        { label: 'DBH', value: bands.dbh },
        { label: 'Height', value: bands.height },
        { label: 'Stump', value: TREE_CART_DEFAULT_STUMP },
        { label: 'Haul wood', value: TREE_CART_DEFAULT_HAUL },
        { label: 'Obstructions', value: String(TREE_CART_DEFAULT_NO_OBST) },
    ];

    return (
        <div className="pci-ai-designs__defaults" ref={wrapRef}>
            <button
                type="button"
                className={`pci-ai-designs__info-btn${open ? ' pci-ai-designs__info-btn--on' : ''}`}
                aria-expanded={open}
                aria-haspopup="dialog"
                aria-label="Tree cutting default options"
                title="Tree cutting default options"
                onClick={() => onOpenChange(!open)}
            >
                <Info size={13} weight="bold" />
            </button>
            {open ? (
                <div
                    className="pci-ai-designs__defaults-pop"
                    role="dialog"
                    aria-label="Tree cutting default options"
                >
                    <p>Estimated cut cost uses these Tree Calculator defaults:</p>
                    <dl>
                        {rows.map((row) => (
                            <div key={row.label}>
                                <dt>{row.label}</dt>
                                <dd>{row.value}</dd>
                            </div>
                        ))}
                    </dl>
                </div>
            ) : null}
        </div>
    );
}

const AiDesignOptionCard: React.FC<AiDesignOptionCardProps> = ({
    option,
    optionNumber,
    rankLabel,
    selected,
    onSelect,
    onApply,
    onOpen3dView,
    viewportAvailable,
    targetKwh,
    applyDisabled = false,
}) => {
    const handleSelect = () => onSelect(option.id);
    const handleCardKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        handleSelect();
    };
    const handleApply = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        onApply(option.id);
    };
    const handleOpen3dView = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        onOpen3dView(option.id);
    };
    const offsetPercent = option.offsetPercent;
    const offsetLabel = offsetPercent != null ? `${offsetPercent}%` : '—';
    const offsetTone =
        offsetPercent == null
            ? 'none'
            : offsetPercent >= 100
              ? 'over'
              : offsetPercent >= 92
                ? 'near'
                : 'under';
    const targetLabel = targetKwh > 0 ? formatKwh(targetKwh) : '—';
    const cut = option.treeCut;
    const cutTreesLabel = cut?.treeLabels.length ? cut.treeLabels.join(', ') : 'tree';
    const cutGain =
        cut?.annualKwhGain != null && cut.annualKwhGain !== 0
            ? `${cut.annualKwhGain > 0 ? '+' : ''}${Math.round(cut.annualKwhGain).toLocaleString()} kWh vs layout`
            : null;
    const previewLayouts = [...option.layouts].sort((a, b) => a.roofIndex - b.roofIndex);
    const roofCount = option.roofLabels.length;
    const isBest = option.rank === 1;

    return (
        <article
            className={`pci-ai-design-card${selected ? ' pci-ai-design-card--selected' : ''}${
                isBest ? ' pci-ai-design-card--best' : ''
            }`}
            aria-pressed={selected}
            tabIndex={0}
            onClick={handleSelect}
            onKeyDown={handleCardKeyDown}
        >
            <div className="pci-ai-design-card__shine" aria-hidden />
            <div className="pci-ai-design-card__body">
                <div className="pci-ai-design-card__main">
            <header className="pci-ai-design-card__head">
                <div className="pci-ai-design-card__identity">
                    <span className="pci-ai-design-card__index">{optionNumber}</span>
                    <div className="pci-ai-design-card__tags">
                        {isBest ? (
                            <span className="pci-ai-design-card__tag pci-ai-design-card__tag--best">
                                <Sparkle size={11} weight="fill" aria-hidden />
                                {rankLabel ?? 'Top pick'}
                            </span>
                        ) : (
                            <span className="pci-ai-design-card__tag pci-ai-design-card__tag--alt">
                                {rankLabel ?? `Option ${optionNumber}`}
                            </span>
                        )}
                        {option.fillOrientation ? (
                            <span className="pci-ai-design-card__tag pci-ai-design-card__tag--orient">
                                {fillOrientationLabel(option.fillOrientation)}
                            </span>
                        ) : null}
                    </div>
                </div>
                <button
                    type="button"
                    className="pci-ai-design-card__view3d"
                    aria-label="Open 3D view"
                    title="3D view"
                    disabled={!viewportAvailable}
                    onClick={handleOpen3dView}
                >
                    <Cube size={14} weight="duotone" aria-hidden />
                    3D
                </button>
            </header>

            <div className="pci-ai-design-card__hero">
                <div className="pci-ai-design-card__production">
                    <span className="pci-ai-design-card__production-label">kWh Actual</span>
                    <strong className="pci-ai-design-card__production-value">
                        {formatKwh(option.annualKwh)}
                    </strong>
                    {targetKwh > 0 ? (
                        <span className="pci-ai-design-card__production-target">
                            Target {targetLabel}
                        </span>
                    ) : null}
                </div>
                {offsetPercent != null ? (
                    <div
                        className={`pci-ai-design-card__offset pci-ai-design-card__offset--${offsetTone}`}
                        aria-label={`${offsetLabel} offset`}
                    >
                        <svg className="pci-ai-design-card__offset-ring" viewBox="0 0 36 36" aria-hidden>
                            <circle className="pci-ai-design-card__offset-track" cx="18" cy="18" r="15.5" />
                            <circle
                                className="pci-ai-design-card__offset-fill"
                                cx="18"
                                cy="18"
                                r="15.5"
                                pathLength={100}
                                strokeDasharray={`${Math.min(100, Math.max(0, offsetPercent))} 100`}
                            />
                        </svg>
                        <span className="pci-ai-design-card__offset-value">{offsetLabel}</span>
                        <em>offset</em>
                    </div>
                ) : null}
            </div>

            <p className="pci-ai-design-card__reason">{option.reason}</p>

            {cut ? (
                <div className="pci-ai-design-card__cut">
                    <div className="pci-ai-design-card__cut-main">
                        <span>Cut {cutTreesLabel}</span>
                        <strong>{formatCutCost(cut.cutCost)}</strong>
                    </div>
                    {cutGain ? <span className="pci-ai-design-card__cut-gain">{cutGain}</span> : null}
                </div>
            ) : null}

            <div className="pci-ai-design-card__metrics">
                <div className="pci-ai-design-card__metric">
                    <span>System</span>
                    <strong>{formatKw(option.systemKw)}</strong>
                </div>
                <div className="pci-ai-design-card__metric">
                    <span>Modules</span>
                    <strong>{option.moduleCount}</strong>
                </div>
                <div className="pci-ai-design-card__metric">
                    <span>Avg SA</span>
                    <strong>{option.averageSolarAccess.toFixed(0)}%</strong>
                </div>
                <div className="pci-ai-design-card__metric">
                    <span>Roofs</span>
                    <strong>{roofCount > 0 ? roofCount : '—'}</strong>
                </div>
            </div>
                </div>

                <div className="pci-ai-design-card__canvas">
                    <div className="pci-ai-design-card__canvas-head">
                        <span>Layout map</span>
                        <span>
                            {roofCount > 0 ? option.roofLabels.join(' · ') : 'No roofs'}
                        </span>
                    </div>
                    <div className="pci-ai-design-card__map" aria-hidden>
                        {previewLayouts.map((layout) => (
                            <MiniRoofPreview
                                key={`${option.id}-${layout.roofIndex}`}
                                layout={layout}
                                unused={option.roofSelections[layout.roofIndex] === false}
                            />
                        ))}
                    </div>
                </div>
            </div>

            <footer className="pci-ai-design-card__foot">
                {selected ? (
                    <>
                        <Check size={13} weight="bold" aria-hidden />
                        Selected
                    </>
                ) : (
                    <span>Tap to preview</span>
                )}
                <button
                    type="button"
                    className="pci-ai-design-card__apply"
                    disabled={applyDisabled}
                    onClick={handleApply}
                >
                    Apply
                </button>
            </footer>
        </article>
    );
};

function MiniModuleCell({
    cell,
}: {
    cell: PreContractRoofModuleLayout['modules'][number];
}) {
    if (!cell) {
        return <span className="pci-ai-mini-mod pci-ai-mini-mod--gap" />;
    }
    return (
        <span
            className={`pci-ai-mini-mod${cell.included ? '' : ' pci-ai-mini-mod--off'}`}
            style={{ backgroundColor: getPreContractModuleShadeColor(cell) }}
        />
    );
}

function MiniRoofPreview({
    layout,
    unused,
}: {
    layout: PreContractRoofModuleLayout;
    unused: boolean;
}) {
    const hasPresent = layout.modules.some(Boolean);
    if (!hasPresent) return null;
    const hasIncluded = layout.modules.some((cell) => cell?.included);

    return (
        <div
            className={`pci-ai-mini-roof pci-ai-mini-roof--${layout.moduleOrientation ?? 'portrait'}${
                unused || !hasIncluded ? ' pci-ai-mini-roof--unused' : ''
            }`}
            title={layout.label}
            style={{ ['--pci-ai-roof-cols' as string]: String(layout.cols) }}
        >
            <span className="pci-ai-mini-roof__label">{layout.label}</span>
            <div className="pci-ai-mini-roof__grid">
                {layout.modules.map((cell, index) => (
                    <MiniModuleCell
                        key={cell?.id ?? `gap-${layout.roofIndex}-${index}`}
                        cell={cell}
                    />
                ))}
            </div>
        </div>
    );
}

export default PreContractAiDesignsStep;
